import { assign, assertEvent, raise, sendParent, setup } from 'xstate';

import type { ScheduleDomainEvent } from '@/features/schedule/state/events';
import type {
  RefreshScheduledBusesQueryQuery,
  ViewNextScheduledBusesQueryQuery,
} from '@/lib/gql/__generated__/graphql';
import { chooseStopSelectionActor, refreshScheduledBusesActor, viewNextScheduledBusesActor } from './selection.actors';
import type { SelectionEvent } from './events';

type SelectionContext = {
  next: ViewNextScheduledBusesQueryQuery['viewNextScheduledBuses'] | undefined;
  refreshed: RefreshScheduledBusesQueryQuery['refreshScheduledBuses'] | undefined;
  error: unknown;
  message: string | undefined;
};

const errorMessage = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export const selectionMachine = setup({
  types: {
    context: {} as SelectionContext,
    events: {} as SelectionEvent | ScheduleDomainEvent,
  },
  actors: {
    viewNextScheduledBusesActor,
    refreshScheduledBusesActor,
    chooseStopSelectionActor,
  },
}).createMachine({
  id: 'selection',
  type: 'parallel',
  context: {
    next: undefined,
    refreshed: undefined,
    error: undefined,
    message: undefined,
  },
  states: {
    next: {
      initial: 'idle',
      states: {
        idle: {
          on: { 'selection.load-next': 'loading' },
        },
        loading: {
          invoke: {
            id: 'view-next-scheduled-buses',
            src: 'viewNextScheduledBusesActor',
            onDone: {
              target: 'ready',
              actions: assign({
                next: ({ event }) => event.output,
                error: () => undefined,
                message: () => undefined,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({ event }) => event.error,
                message: ({ event }) => errorMessage(event.error, 'We could not load the next buses.'),
              }),
            },
          },
        },
        ready: {
          on: {
            'selection.load-next': 'loading',
            'selection.changed': 'loading',
            'schedule.published': 'loading',
          },
        },
        error: {
          on: {
            'selection.load-next': 'loading',
            'selection.changed': 'loading',
            'schedule.published': 'loading',
          },
        },
      },
    },
    refresh: {
      initial: 'idle',
      states: {
        idle: {
          on: { 'selection.load-refresh': 'loading' },
        },
        loading: {
          invoke: {
            id: 'refresh-scheduled-buses',
            src: 'refreshScheduledBusesActor',
            onDone: {
              target: 'ready',
              actions: assign({
                refreshed: ({ event }) => event.output,
                error: () => undefined,
                message: () => undefined,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({ event }) => event.error,
                message: ({ event }) => errorMessage(event.error, 'We could not refresh the next buses.'),
              }),
            },
          },
        },
        ready: {
          on: {
            'selection.load-refresh': 'loading',
            'selection.changed': 'loading',
            'schedule.published': 'loading',
          },
        },
        error: {
          on: {
            'selection.load-refresh': 'loading',
            'selection.changed': 'loading',
            'schedule.published': 'loading',
          },
        },
      },
    },
    write: {
      initial: 'idle',
      states: {
        idle: {
          on: { 'selection.choose': 'saving' },
        },
        saving: {
          invoke: {
            id: 'choose-stop-selection',
            src: 'chooseStopSelectionActor',
            input: ({ event }) => {
              assertEvent(event, 'selection.choose');
              return {
                selectionId: crypto.randomUUID(),
                route: event.input.route,
                direction: event.input.direction,
                stopId: event.input.stopId,
                stopName: event.input.stopName,
              };
            },
            onDone: {
              target: 'ready',
              actions: [
                assign({ error: () => undefined, message: () => undefined }),
                raise({ type: 'selection.changed' }),
                sendParent({ type: 'selection.chosen' }),
                sendParent({
                  type: 'ui.toast.push',
                  toast: {
                    title: 'Stop choice saved',
                    tone: 'success',
                  },
                }),
              ],
            },
            onError: {
              target: 'error',
              actions: [
                assign({
                  error: ({ event }) => event.error,
                  message: ({ event }) => errorMessage(event.error, 'We could not save your stop choice.'),
                }),
                sendParent({
                  type: 'ui.toast.push',
                  toast: {
                    title: 'Stop choice was not saved',
                    tone: 'error',
                  },
                }),
              ],
            },
          },
        },
        ready: {
          on: { 'selection.choose': 'saving' },
        },
        error: {
          on: { 'selection.choose': 'saving' },
        },
      },
    },
  },
});
